import React, { useState, useEffect, useMemo } from 'react';
import { useAuth, apiClient } from '@/lib/api';
import { toast } from 'sonner';
import logger from '@/lib/logger';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    ArrowRightLeft, Check, X, Clock, Users, ChevronRight,
    Shield, AlertTriangle, MessageSquare, Search,
} from 'lucide-react';

const TransferRequestsPage = () => {
    const { user } = useAuth();
    const [requests, setRequests] = useState([]);
    const [agents, setAgents] = useState([]);
    const [leads, setLeads] = useState([]);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [entitiesLoading, setEntitiesLoading] = useState(false);
    const [tab, setTab] = useState('pending');
    const [showNewRequest, setShowNewRequest] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [approvalComment, setApprovalComment] = useState('');
    const [form, setForm] = useState({ entity_type: '', entity_id: '', to_agent_id: '', reason: '' });
    const [entitySearch, setEntitySearch] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchAll(); }, [tab]);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const statusFilter = tab === 'pending' ? '' : tab === 'awaiting_ceo' ? 'pending_final_approval' : tab;
            const [reqRes, agentsRes] = await Promise.all([
                statusFilter
                    ? apiClient.get(`/transfers/requests?status=${statusFilter}`)
                    : apiClient.get('/transfers/requests'),
                apiClient.get('/users'),
            ]);
            let filtered = reqRes.data || [];
            if (tab === 'pending') {
                // anything still pending (any level not yet final/approved/rejected)
                filtered = filtered.filter(r => r.status !== 'approved' && r.status !== 'rejected' && r.status !== 'pending_final_approval');
            }
            setRequests(filtered);
            setAgents(agentsRes.data?.filter(u => u.is_active) || []);
        } catch (e) { logger.error(e); }
        setLoading(false);
    };

    const fetchEntities = async (type) => {
        setEntitiesLoading(true);
        try {
            if (type === 'lead') {
                const res = await apiClient.get('/leads?limit=5000');
                // /leads may return either an array or a paginated { items, total, ... } object
                setLeads(res.data?.items || (Array.isArray(res.data) ? res.data : []));
            } else {
                const res = await apiClient.get('/students?limit=5000');
                setStudents(res.data?.items || (Array.isArray(res.data) ? res.data : []));
            }
        } catch (e) { logger.error(e); }
        setEntitiesLoading(false);
    };

    const handleSubmit = async () => {
        if (!form.entity_type || !form.entity_id || !form.to_agent_id || !form.reason) {
            toast.error('All fields required'); return;
        }
        setSubmitting(true);
        try {
            await apiClient.post('/transfers/request', form);
            toast.success('Transfer request submitted');
            setShowNewRequest(false);
            setForm({ entity_type: '', entity_id: '', to_agent_id: '', reason: '' });
            setEntitySearch('');
            fetchAll();
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
        setSubmitting(false);
    };

    const handleApproval = async (action) => {
        if (!selectedRequest) return;
        setSubmitting(true);
        try {
            const res = await apiClient.post(`/transfers/${selectedRequest.id}/approve`, { action, comment: approvalComment });
            toast.success(res.data.message);
            setShowApproveModal(false);
            setSelectedRequest(null);
            setApprovalComment('');
            fetchAll();
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
        setSubmitting(false);
    };

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
    const typeLabel = (t) => t === 'lead' ? 'Sales Lead' : t === 'student' ? 'CS Student' : 'Mentor Student';
    const typeColor = (t) => t === 'lead' ? 'text-blue-500' : t === 'student' ? 'text-emerald-500' : 'text-orange-500';
    const statusBadge = (s) => {
        if (s === 'pending_first_approval' || s === 'pending_team_leader_approval') return <Badge variant="secondary" className="bg-amber-500/20 text-amber-600">Pending Team Leader</Badge>;
        if (s === 'pending_sales_manager_approval') return <Badge variant="secondary" className="bg-orange-500/20 text-orange-600">Pending Sales Manager</Badge>;
        if (s === 'pending_cs_head_approval') return <Badge variant="secondary" className="bg-fuchsia-500/20 text-fuchsia-600">Pending CS Head</Badge>;
        if (s === 'pending_final_approval') return <Badge variant="secondary" className="bg-blue-500/20 text-blue-600">Awaiting CEO</Badge>;
        if (s === 'approved') return <Badge variant="secondary" className="bg-emerald-500/20 text-emerald-600">Approved</Badge>;
        if (s === 'rejected') return <Badge variant="destructive">Rejected</Badge>;
        return <Badge variant="outline">{s}</Badge>;
    };

    const canApprove = (req) => {
        const role = user?.role;
        const level = req.current_approver_level || req.first_approver_role;
        if (['super_admin', 'admin'].includes(role)) return req.status !== 'approved' && req.status !== 'rejected';
        const roleLevelMap = {
            team_leader: ['team_leader'],
            sales_manager: ['sales_manager'],
            cs_head: ['cs_head'],
            hr: ['hr'],
        };
        return (roleLevelMap[role] || []).includes(level);
    };

    // Filter leads/students by search
    const filteredEntities = useMemo(() => {
        const q = entitySearch.trim().toLowerCase();
        if (form.entity_type === 'lead') {
            const base = leads;
            if (!q) return base.slice(0, 200);
            return base.filter(l =>
                (l.full_name || '').toLowerCase().includes(q) ||
                (l.phone || '').includes(q) ||
                (l.email || '').toLowerCase().includes(q) ||
                (l.assigned_to_name || '').toLowerCase().includes(q)
            ).slice(0, 200);
        }
        const base = students;
        if (!q) return base.slice(0, 200);
        return base.filter(s =>
            (s.full_name || '').toLowerCase().includes(q) ||
            (s.phone || '').includes(q) ||
            (s.email || '').toLowerCase().includes(q) ||
            (s.cs_agent_name || '').toLowerCase().includes(q) ||
            (s.mentor_name || '').toLowerCase().includes(q)
        ).slice(0, 200);
    }, [entitySearch, form.entity_type, leads, students]);

    const salesAgents = useMemo(() => {
        if (form.entity_type === 'lead') {
            return agents.filter(a => ['sales_executive', 'team_leader'].includes(a.role));
        }
        if (form.entity_type === 'student') return agents.filter(a => ['cs_agent', 'cs_head'].includes(a.role));
        if (form.entity_type === 'mentor_student') return agents.filter(a => a.role === 'mentor');
        return [];
    }, [form.entity_type, agents]);

    return (
        <div className="space-y-6" data-testid="transfer-requests-page">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                        <ArrowRightLeft className="h-8 w-8 text-primary" /> Transfer Requests
                    </h1>
                    <p className="text-muted-foreground">Lead, student & mentor transfers with multi-level approval</p>
                </div>
                <Button onClick={() => { setShowNewRequest(true); setForm({ entity_type: '', entity_id: '', to_agent_id: '', reason: '' }); setEntitySearch(''); }} data-testid="new-transfer-btn">
                    <ArrowRightLeft className="h-4 w-4 mr-2" /> New Transfer Request
                </Button>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="pending">Pending Approval</TabsTrigger>
                    <TabsTrigger value="awaiting_ceo">Awaiting CEO</TabsTrigger>
                    <TabsTrigger value="approved">Approved</TabsTrigger>
                    <TabsTrigger value="rejected">Rejected</TabsTrigger>
                </TabsList>

                <TabsContent value={tab}>
                    <Card>
                        <CardContent className="pt-4">
                            {loading ? (
                                <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div></div>
                            ) : requests.length === 0 ? (
                                <div className="text-center text-muted-foreground py-12">
                                    <ArrowRightLeft className="h-12 w-12 mx-auto mb-3 opacity-30" />
                                    <p>No {tab === 'pending' ? 'pending' : tab} transfer requests</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Entity</TableHead>
                                            <TableHead>From</TableHead>
                                            <TableHead>To</TableHead>
                                            <TableHead>Reason</TableHead>
                                            <TableHead>Requested By</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Date</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {requests.map(req => (
                                            <TableRow key={req.id} data-testid={`transfer-row-${req.id}`}>
                                                <TableCell><span className={`font-medium text-sm ${typeColor(req.entity_type)}`}>{typeLabel(req.entity_type)}</span></TableCell>
                                                <TableCell className="font-medium">{req.entity_name}</TableCell>
                                                <TableCell>
                                                    <div><p className="text-sm">{req.from_agent_name}</p>{req.from_team && <p className="text-xs text-muted-foreground">{req.from_team}</p>}</div>
                                                </TableCell>
                                                <TableCell>
                                                    <div><p className="text-sm font-medium">{req.to_agent_name}</p>{req.to_team && <p className="text-xs text-muted-foreground">{req.to_team}</p>}</div>
                                                </TableCell>
                                                <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">{req.reason}</TableCell>
                                                <TableCell className="text-sm">{req.requested_by_name}</TableCell>
                                                <TableCell>{statusBadge(req.status)}</TableCell>
                                                <TableCell className="text-xs text-muted-foreground">{formatDate(req.created_at)}</TableCell>
                                                <TableCell className="text-right">
                                                    {canApprove(req) && (
                                                        <div className="flex items-center gap-1 justify-end">
                                                            <Button size="sm" variant="ghost" className="text-emerald-500" onClick={() => { setSelectedRequest(req); setApprovalComment(''); setShowApproveModal(true); }} data-testid={`approve-btn-${req.id}`}>
                                                                <Check className="h-4 w-4" />
                                                            </Button>
                                                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => { setSelectedRequest(req); setApprovalComment(''); setShowApproveModal(true); }} data-testid={`reject-btn-${req.id}`}>
                                                                <X className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    )}
                                                    {(req.approval_history || []).map((h, idx) => (
                                                        <p key={idx} className={`text-[10px] mt-0.5 ${h.action === 'approve' ? 'text-emerald-500' : 'text-red-500'}`}>
                                                            {h.level}: {h.approved_by_name}
                                                        </p>
                                                    ))}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* New Transfer Request Modal */}
            <Dialog open={showNewRequest} onOpenChange={setShowNewRequest}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>New Transfer Request</DialogTitle>
                        <DialogDescription>Request a transfer. Sales leads need Team Leader → Sales Manager → CEO approval.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium">Transfer Type</label>
                            <Select value={form.entity_type} onValueChange={(v) => { setForm({ ...form, entity_type: v, entity_id: '', to_agent_id: '' }); setEntitySearch(''); fetchEntities(v); }}>
                                <SelectTrigger data-testid="transfer-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="lead">Sales Lead (Team Leader → Sales Manager → CEO)</SelectItem>
                                    <SelectItem value="student">CS Student (CS Head → CEO)</SelectItem>
                                    <SelectItem value="mentor_student">Mentor Student (CEO only)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {form.entity_type && (
                            <div>
                                <label className="text-sm font-medium flex items-center justify-between">
                                    <span>Select {form.entity_type === 'lead' ? 'Lead' : 'Student'}</span>
                                    {form.entity_id && (
                                        <span className="text-xs text-emerald-500">
                                            ✓ Selected: {(form.entity_type === 'lead' ? leads : students).find(x => x.id === form.entity_id)?.full_name}
                                        </span>
                                    )}
                                </label>
                                <div className="mt-1.5 space-y-2">
                                    <div className="relative">
                                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            value={entitySearch}
                                            onChange={(e) => setEntitySearch(e.target.value)}
                                            placeholder={`Search by name, phone, email, or current agent…`}
                                            className="pl-9"
                                            data-testid="entity-search-input"
                                        />
                                    </div>
                                    <div className="max-h-60 overflow-y-auto rounded-md border border-border bg-muted/20">
                                        {entitiesLoading ? (
                                            <div className="p-4 text-sm text-muted-foreground text-center">Loading…</div>
                                        ) : filteredEntities.length === 0 ? (
                                            <div className="p-4 text-sm text-muted-foreground text-center">
                                                {entitySearch ? 'No matches' : `No ${form.entity_type === 'lead' ? 'leads' : 'students'} available`}
                                            </div>
                                        ) : (
                                            filteredEntities.map(x => (
                                                <button
                                                    key={x.id}
                                                    type="button"
                                                    onClick={() => setForm({ ...form, entity_id: x.id })}
                                                    data-testid={`entity-pick-${x.id}`}
                                                    className={`w-full text-left px-3 py-2 text-sm border-b border-border/60 last:border-b-0 transition-colors hover:bg-primary/10 ${form.entity_id === x.id ? 'bg-primary/20' : ''}`}
                                                >
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium truncate">{x.full_name}</p>
                                                            <p className="text-xs text-muted-foreground truncate">
                                                                {x.phone || '—'} · {form.entity_type === 'lead' ? (x.assigned_to_name || 'Unassigned') : (form.entity_type === 'student' ? (x.cs_agent_name || 'No CS') : (x.mentor_name || 'No Mentor'))}
                                                            </p>
                                                        </div>
                                                        {form.entity_type === 'lead' && x.stage && (
                                                            <Badge variant="outline" className="text-[10px] shrink-0">{x.stage}</Badge>
                                                        )}
                                                    </div>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                    <p className="text-[11px] text-muted-foreground">
                                        Showing {filteredEntities.length} of {(form.entity_type === 'lead' ? leads : students).length} · type to search
                                    </p>
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="text-sm font-medium">Transfer To {form.entity_type === 'lead' ? 'Sales Agent' : form.entity_type === 'student' ? 'CS Agent' : 'Mentor'}</label>
                            <Select value={form.to_agent_id} onValueChange={(v) => setForm({ ...form, to_agent_id: v })}>
                                <SelectTrigger data-testid="transfer-to-agent"><SelectValue placeholder="Select target agent" /></SelectTrigger>
                                <SelectContent>
                                    {salesAgents.map(a => (
                                        <SelectItem key={a.id} value={a.id}>
                                            {a.full_name} {a.role === 'team_leader' ? '(Team Leader)' : ''} {a.team_name ? `— ${a.team_name}` : ''}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <label className="text-sm font-medium">Reason for Transfer</label>
                            <Textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Explain why this transfer is needed…" className="min-h-[80px]" data-testid="transfer-reason" />
                        </div>

                        <div className="p-3 bg-muted/50 rounded-lg">
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Shield className="h-3 w-3" /> Approval Chain: {form.entity_type === 'lead' ? 'Team Leader → Sales Manager → CEO' : form.entity_type === 'student' ? 'CS Head → CEO' : 'CEO Only'}
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowNewRequest(false)}>Cancel</Button>
                        <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-transfer-btn">{submitting ? 'Submitting…' : 'Submit Request'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Approve/Reject Modal */}
            <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Review Transfer Request</DialogTitle>
                        <DialogDescription>
                            Transfer {selectedRequest?.entity_name} from {selectedRequest?.from_agent_name} to {selectedRequest?.to_agent_name}
                        </DialogDescription>
                    </DialogHeader>
                    {selectedRequest && (
                        <div className="space-y-3">
                            <div className="p-3 bg-muted/50 rounded-lg space-y-1">
                                <p className="text-sm"><span className="text-muted-foreground">Type:</span> {typeLabel(selectedRequest.entity_type)}</p>
                                <p className="text-sm"><span className="text-muted-foreground">From:</span> {selectedRequest.from_agent_name} {selectedRequest.from_team && `(${selectedRequest.from_team})`}</p>
                                <p className="text-sm"><span className="text-muted-foreground">To:</span> {selectedRequest.to_agent_name} {selectedRequest.to_team && `(${selectedRequest.to_team})`}</p>
                                <p className="text-sm"><span className="text-muted-foreground">Reason:</span> {selectedRequest.reason}</p>
                                <p className="text-sm"><span className="text-muted-foreground">Requested by:</span> {selectedRequest.requested_by_name}</p>
                                {(selectedRequest.approval_history || []).map((h, idx) => (
                                    <p key={idx} className="text-sm text-emerald-500">
                                        Approved at {h.level} by {h.approved_by_name}
                                    </p>
                                ))}
                            </div>
                            <div>
                                <label className="text-sm font-medium">Comment (optional)</label>
                                <Textarea value={approvalComment} onChange={e => setApprovalComment(e.target.value)} placeholder="Add a comment…" />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowApproveModal(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={() => handleApproval('reject')} disabled={submitting}>Reject</Button>
                        <Button onClick={() => handleApproval('approve')} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">{submitting ? 'Processing…' : 'Approve'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default TransferRequestsPage;
